import { useTranslation } from 'react-i18next'
import { addDays, startOfDay } from 'date-fns'
import { useTaskScheduleEditor } from '../hooks/useTaskScheduleEditor'
import { useProjects } from '../hooks/useProjects'
import { DateDurationPicker } from './DateDurationPicker'
import { AssigneeComboBox } from './AssigneeComboBox'
import { ProjectComboBox } from './ProjectComboBox'
import type { ReactNode } from 'react'
import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'
import { PickerPanel } from '@/components/app/combo-box/PickerPanel'
import { cn } from '@/lib/utils'

type Apply = (updates: Partial<TaskDocument>) => void

const SCHEDULE_OFFSETS = { today: 0, tomorrow: 1, nextWeek: 7, none: null } as const
type ScheduleKey = keyof typeof SCHEDULE_OFFSETS

/**
 * Quick schedule presets on the shared picker shell, for places that set one date
 * on many tasks (bulk bar) or on a task that doesn't exist yet (sub-task quick add).
 * A saved task gets the full picker instead: see TaskRowDatePicker.
 */
export function SchedulePanel({ onApply }: { onApply: Apply }) {
  const { t } = useTranslation('tasks')
  const labels: Record<ScheduleKey, string> = {
    today: t('grouping.today'),
    tomorrow: t('grouping.tomorrow'),
    nextWeek: t('bulk.nextWeek'),
    none: t('grouping.noDate'),
  }
  const options = (Object.keys(SCHEDULE_OFFSETS) as Array<ScheduleKey>).map((key, index) => ({
    value: key,
    label: labels[key],
    shortcut: String(index + 1),
  }))
  return (
    <PickerPanel
      options={options}
      searchPlaceholder={t('bulk.scheduleSearch')}
      onSelect={(value) => {
        const offset = SCHEDULE_OFFSETS[value as ScheduleKey]
        onApply(
          offset === null
            ? { dueDate: null, dueDateType: null }
            : { dueDate: addDays(startOfDay(new Date()), offset).getTime(), dueDateType: 'date', isSomeday: false },
        )
      }}
    />
  )
}

interface TaskRowDatePickerProps {
  task: TaskDocument
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The closed trigger: a button that calls `onOpenChange(true)` when clicked. */
  trigger: ReactNode
}

/**
 * The task detail's date picker (dates, reminder, repeat) on a task row. The
 * reminder query only runs while it's open, so a closed picker in every row costs nothing.
 */
export function TaskRowDatePicker({ task, open, onOpenChange, trigger }: TaskRowDatePickerProps) {
  if (!open) return <>{trigger}</>
  return <OpenTaskRowDatePicker task={task} onOpenChange={onOpenChange} trigger={trigger} />
}

function OpenTaskRowDatePicker({ task, onOpenChange, trigger }: Omit<TaskRowDatePickerProps, 'open'>) {
  const { props, isLoading } = useTaskScheduleEditor(task)
  // The picker seeds its draft from props when it opens, so wait for the reminder.
  return <DateDurationPicker {...props} open={!isLoading} onOpenChange={onOpenChange} trigger={trigger} />
}

interface RowPickerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Ghost icon button classes, shared with the row's other actions. */
  triggerClassName: string
  icon: ReactNode
  label: string
}

/** Strips the combobox's outline-button chrome so the open trigger matches the closed one. */
const GHOST_TRIGGER =
  'h-auto border-0 bg-transparent p-0 shadow-none data-[state=open]:border-transparent data-[state=open]:shadow-none'

function ClosedTrigger({ onOpenChange, triggerClassName, icon, label }: Omit<RowPickerProps, 'open'>) {
  return (
    <button
      type="button"
      className={triggerClassName}
      aria-label={label}
      title={label}
      onClick={() => onOpenChange(true)}
    >
      {icon}
    </button>
  )
}

/**
 * Assignee and project pickers on a task row, the same ones the task detail and
 * create task use. Mounted only while open, so rows don't each subscribe to
 * members and projects.
 */
export function TaskRowAssigneePicker({ task, onApply, ...trigger }: RowPickerProps & { task: TaskDocument; onApply: Apply }) {
  if (!trigger.open) return <ClosedTrigger {...trigger} />
  return <OpenAssigneePicker task={task} onApply={onApply} {...trigger} />
}

function OpenAssigneePicker({ task, onApply, onOpenChange, triggerClassName, icon, label }: RowPickerProps & { task: TaskDocument; onApply: Apply }) {
  const { projects } = useProjects()
  return (
    <AssigneeComboBox
      projectId={task.projectId ?? null}
      selectedUserId={task.userId || null}
      onUserChange={(userId) => onApply({ userId })}
      allowSuperAgent={projects.find((p) => p.id === task.projectId)?.superAgentEnabled ?? false}
      popoverOpen
      onPopoverOpenChange={onOpenChange}
      buttonClassName={cn(triggerClassName, GHOST_TRIGGER)}
      triggerContent={icon}
      triggerProps={{ 'aria-label': label, title: label }}
    />
  )
}

export function TaskRowProjectPicker({ task, onApply, ...trigger }: RowPickerProps & { task: TaskDocument; onApply: Apply }) {
  if (!trigger.open) return <ClosedTrigger {...trigger} />
  return <OpenProjectPicker task={task} onApply={onApply} {...trigger} />
}

function OpenProjectPicker({ task, onApply, onOpenChange, triggerClassName, icon, label }: RowPickerProps & { task: TaskDocument; onApply: Apply }) {
  const { t } = useTranslation('tasks')
  const { projects } = useProjects()
  return (
    <ProjectComboBox
      projects={projects}
      selectedProjectId={task.projectId || null}
      onProjectChange={(projectId) => onApply({ projectId })}
      searchText={t('bulk.move')}
      popoverOpen
      onPopoverOpenChange={onOpenChange}
      buttonClassName={cn(triggerClassName, GHOST_TRIGGER)}
      triggerContent={icon}
      triggerProps={{ 'aria-label': label, title: label }}
    />
  )
}
